import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

import appdata from '../../../../Services/appdata';
import {
  getParams,
  thousandFormat,
  thousandFormat2,
} from '../../../../Utils/Functions';
import { empty } from '../../../../Assets/js/constant';

const tableBorder = {
  borderBottom: '1px solid lightgrey',
  borderRight: '1px solid lightgrey',
  height: 20,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const BottomChart = ({
  detailData,
  selectedTopItems,
  tableStructure,
  profitName,
  pprofitName,
  selectedOption,
  selectedYears,
  showPercent,
  handleFilter,
}) => {
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedIdRects, setSelectedIdRects] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [pageOffset, setPageOffset] = useState(0);
  const [filterStructure, setFilterStructure] = useState([]);
  const [profit, setProfit] = useState({});
  const [pprofit, setPprofit] = useState({});

  const isMountedRef = useRef(false);

  const onResize = () => {
    if (isMountedRef.current) {
      setResize(prev => !prev);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    window.addEventListener('resize', onResize);
    prepareData();

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (isMountedRef.current) {
      prepareData();
    }
  }, [detailData, selectedTopItems, tableStructure]);

  const prepareData = async () => {
    const sortedSelectedTopItems = [...selectedTopItems].sort((a, b) => a['year'] - b['year']);
    setSelectedItems(sortedSelectedTopItems);
    
    let profitData = {};
    let pprofitData = {};
    let alreadyIncluded = {};
    let filterStructure = [];
    let tableStructureData = [];

    if (detailData.length > 0) {
      if (detailData.length > 0) {
        if (appdata.getItem('tableStructure'))
          tableStructureData = appdata.getItem('tableStructure');

        tableStructureData.forEach((structure) => {
          let filterAccount = structure;
          let subAccounts = structure.subItems;
          let removeAccounts = [];
          if (structure.total) {
            structure.subItems.forEach((item) => {
              let accounts = detailData.filter(
                (account) => account.IncomeStatementCategory === item.content,
              );

              if (accounts.length > 0) {
                sortedSelectedTopItems.forEach((topItem) => {
                  item[topItem.year] = {
                    Actual: 0,
                    Budget: 0,
                    Forecast: 0,
                    ActualVsBudgetVariance: 0,
                    ActualVsForecastVariance: 0,
                    YTDActual: 0,
                    YTDBudget: 0,
                    YTDForecast: 0,
                    YTDActualVsBudgetVariance: 0,
                    YTDActualVsForecastVariance: 0,
                  };
                  accounts.forEach((account) => {
                    if (account.FY === topItem.year) {
                      item[topItem.year].Actual = parseFloat(account.Actual);
                      item[topItem.year].Budget = parseFloat(account.Budget);
                      item[topItem.year].Forecast = parseFloat(
                        account.Forecast,
                      );
                      item[topItem.year].ActualVsBudgetVariance = parseFloat(
                        account.ActualVsBudgetVariance,
                      );
                      item[topItem.year].ActualVsForecastVariance = parseFloat(
                        account.ActualVsForecastVariance,
                      );
                      item[topItem.year].YTDActual = parseFloat(
                        account.YTDActual,
                      );
                      item[topItem.year].YTDBudget = parseFloat(
                        account.YTDBudget,
                      );
                      item[topItem.year].YTDForecast = parseFloat(
                        account.YTDForecast,
                      );
                      item[topItem.year].YTDActualVsBudgetVariance = parseFloat(
                        account.YTDActualVsBudgetVariance,
                      );
                      item[topItem.year].YTDActualVsForecastVariance =
                        parseFloat(account.YTDActualVsForecastVariance);
                      item.Income = account.Income;
                    }
                  });
                });
              } else removeAccounts.push(item);
            });
            subAccounts = subAccounts.filter(function (e) {
              return !removeAccounts.some(function (s) {
                return s.content === e.content;
              });
            });
            filterAccount.subItems = subAccounts;
            if (subAccounts.length > 0) filterStructure.push(filterAccount);
          } else {
            structure.profit = {};
            structure.pprofit = {};
            filterStructure.push(structure);
          }
        });

        filterStructure.forEach((structure) => {
          if (structure.total) {
            sortedSelectedTopItems.forEach((topItem) => {
              structure[topItem.year] = {};
              structure[topItem.year].totalActual = 0;
              structure[topItem.year].totalBudget = 0;
              structure[topItem.year].totalForecast = 0;
              structure[topItem.year].totalActualVsForecastVariance = 0;
              structure[topItem.year].totalActualVsBudgetVariance = 0;
              structure[topItem.year].totalYTDActual = 0;
              structure[topItem.year].totalYTDBudget = 0;
              structure[topItem.year].totalYTDForecast = 0;
              structure[topItem.year].totalYTDActualVsBudgetVariance = 0;
              structure[topItem.year].totalYTDActualVsForecastVariance = 0;

              if (structure.income) {
                structure.subItems.forEach((item) => {
                  if (item.Income) {
                    structure[topItem.year].totalActual +=
                      item[topItem.year].Actual;
                    structure[topItem.year].totalBudget +=
                      item[topItem.year].Budget;
                    structure[topItem.year].totalForecast +=
                      item[topItem.year].Forecast;
                    structure[topItem.year].totalYTDActual +=
                      item[topItem.year].YTDActual;
                    structure[topItem.year].totalYTDBudget +=
                      item[topItem.year].YTDBudget;
                    structure[topItem.year].totalYTDForecast +=
                      item[topItem.year].YTDForecast;
                  } else {
                    item[topItem.year].Actual = 0 - item[topItem.year].Actual;
                    item[topItem.year].Budget = 0 - item[topItem.year].Budget;
                    item[topItem.year].Forecast =
                      0 - item[topItem.year].Forecast;
                    item[topItem.year].YTDActual =
                      0 - item[topItem.year].YTDActual;
                    item[topItem.year].YTDBudget =
                      0 - item[topItem.year].YTDBudget;
                    item[topItem.year].YTDForecast =
                      0 - item[topItem.year].YTDForecast;
                    structure[topItem.year].totalActual +=
                      item[topItem.year].Actual;
                    structure[topItem.year].totalBudget +=
                      item[topItem.year].Budget;
                    structure[topItem.year].totalForecast +=
                      item[topItem.year].Forecast;
                    structure[topItem.year].totalYTDActual +=
                      item[topItem.year].YTDActual;
                    structure[topItem.year].totalYTDBudget +=
                      item[topItem.year].YTDBudget;
                    structure[topItem.year].totalYTDForecast +=
                      item[topItem.year].YTDForecast;
                  }
                  return item;
                });
                structure[topItem.year].totalActualVsForecastVariance =
                  structure[topItem.year].totalActual -
                  structure[topItem.year].totalForecast;
                structure[topItem.year].totalActualVsBudgetVariance =
                  structure[topItem.year].totalActual -
                  structure[topItem.year].totalBudget;
                structure[topItem.year].totalYTDActualVsBudgetVariance =
                  structure[topItem.year].totalYTDActual -
                  structure[topItem.year].totalYTDForecast;
                structure[topItem.year].totalYTDActualVsForecastVariance =
                  structure[topItem.year].totalYTDActual -
                  structure[topItem.year].totalYTDBudget;
              } else {
                structure.subItems.map((item) => {
                  if (item.Income) {
                    item[topItem.year].Actual = 0 - item[topItem.year].Actual;
                    item[topItem.year].Budget = 0 - item[topItem.year].Budget;
                    item[topItem.year].Forecast =
                      0 - item[topItem.year].Forecast;
                    item[topItem.year].YTDActual =
                      0 - item[topItem.year].YTDActual;
                    item[topItem.year].YTDBudget =
                      0 - item[topItem.year].YTDBudget;
                    item[topItem.year].YTDForecast =
                      0 - item[topItem.year].YTDForecast;
                    structure[topItem.year].totalActual +=
                      item[topItem.year].Actual;
                    structure[topItem.year].totalBudget +=
                      item[topItem.year].Budget;
                    structure[topItem.year].totalForecast +=
                      item[topItem.year].Forecast;
                    structure[topItem.year].totalYTDActual +=
                      item[topItem.year].YTDActual;
                    structure[topItem.year].totalYTDBudget +=
                      item[topItem.year].YTDBudget;
                    structure[topItem.year].totalYTDForecast +=
                      item[topItem.year].YTDForecast;
                  } else {
                    structure[topItem.year].totalActual +=
                      item[topItem.year].Actual;
                    structure[topItem.year].totalBudget +=
                      item[topItem.year].Budget;
                    structure[topItem.year].totalForecast +=
                      item[topItem.year].Forecast;
                    structure[topItem.year].totalYTDActual +=
                      item[topItem.year].YTDActual;
                    structure[topItem.year].totalYTDBudget +=
                      item[topItem.year].YTDBudget;
                    structure[topItem.year].totalYTDForecast +=
                      item[topItem.year].YTDForecast;
                  }
                  return item;
                });
                structure[topItem.year].totalActualVsForecastVariance =
                  structure[topItem.year].totalForecast -
                  structure[topItem.year].totalActual;
                structure[topItem.year].totalActualVsBudgetVariance =
                  structure[topItem.year].totalActual -
                  structure[topItem.year].totalBudget;
                structure[topItem.year].totalYTDActualVsBudgetVariance =
                  structure[topItem.year].totalYTDForecast -
                  structure[topItem.year].totalYTDActual;
                structure[topItem.year].totalYTDActualVsForecastVariance =
                  structure[topItem.year].totalYTDActual -
                  structure[topItem.year].totalYTDBudget;
              }
            });
          } else {
            sortedSelectedTopItems.forEach((topItem) => {
              structure.profit[topItem.year] = {
                incomeActual: 0,
                incomeBudget: 0,
                incomeForecast: 0,
                incomeActualVsBudgetVariance: 0,
                incomeActualVsForecastVariance: 0,
                incomeYTDActual: 0,
                incomeYTDBudget: 0,
                incomeYTDForecast: 0,
                incomeYTDActualVsBudgetVariance: 0,
                incomeYTDActualVsForecastVariance: 0,
                expenseActual: 0,
                expenseBudget: 0,
                expenseForecast: 0,
                expenseActualVsBudgetVariance: 0,
                expenseActualVsForecastVariance: 0,
                expenseYTDActual: 0,
                expenseYTDBudget: 0,
                expenseYTDForecast: 0,
                expenseYTDActualVsBudgetVariance: 0,
                expenseYTDActualVsForecastVariance: 0,
              };
              structure.pprofit[topItem.year] = {
                Actual: 0,
                ActualVsBudgetVariance: 0,
                ActualVsForecastVariance: 0,
                Budget: 0,
                Forecast: 0,
                YTDActual: 0,
                YTDActualVsBudgetVariance: 0,
                YTDActualVsForecastVariance: 0,
                YTDBudget: 0,
                YTDForecast: 0,
              };
            });
          }
        });

        sortedSelectedTopItems.forEach((topItem) => {
          profitData[topItem.year] = {
            incomeActual: 0,
            incomeBudget: 0,
            incomeForecast: 0,
            incomeActualVsBudgetVariance: 0,
            incomeActualVsForecastVariance: 0,
            incomeYTDActual: 0,
            incomeYTDBudget: 0,
            incomeYTDForecast: 0,
            incomeYTDActualVsBudgetVariance: 0,
            incomeYTDActualVsForecastVariance: 0,
            expenseActual: 0,
            expenseBudget: 0,
            expenseForecast: 0,
            expenseActualVsBudgetVariance: 0,
            expenseActualVsForecastVariance: 0,
            expenseYTDActual: 0,
            expenseYTDBudget: 0,
            expenseYTDForecast: 0,
            expenseYTDActualVsBudgetVariance: 0,
            expenseYTDActualVsForecastVariance: 0,
          };
          alreadyIncluded[topItem.year] = {
            Actual: 0,
            Budget: 0,
            Forecast: 0,
            ActualVsBudgetVariance: 0,
            ActualVsForecastVariance: 0,
            YTDActual: 0,
            YTDBudget: 0,
            YTDForecast: 0,
            YTDActualVsBudgetVariance: 0,
            YTDActualVsForecastVariance: 0,
          };
        });

        if (filterStructure.length > 0) {
          let total_index = 0;
          for (let i = 0; i < filterStructure.length; i++) {
            if (filterStructure[i].total) {
              sortedSelectedTopItems.forEach((topItem) => {
                if (filterStructure[i].income) {
                  profitData[topItem.year].incomeActual +=
                    filterStructure[i][topItem.year].totalActual;
                  profitData[topItem.year].incomeBudget +=
                    filterStructure[i][topItem.year].totalBudget;
                  profitData[topItem.year].incomeForecast +=
                    filterStructure[i][topItem.year].totalForecast;
                  profitData[topItem.year].incomeActualVsBudgetVariance +=
                    filterStructure[i][topItem.year].totalActual -
                    filterStructure[i][topItem.year].totalBudget;
                  profitData[topItem.year].incomeActualVsForecastVariance +=
                    filterStructure[i][topItem.year].totalActual -
                    filterStructure[i][topItem.year].totalForecast;
                  profitData[topItem.year].incomeYTDActual +=
                    filterStructure[i][topItem.year].totalYTDActual;
                  profitData[topItem.year].incomeYTDBudget +=
                    filterStructure[i][topItem.year].totalYTDBudget;
                  profitData[topItem.year].incomeYTDForecast +=
                    filterStructure[i][topItem.year].totalYTDForecast;
                  profitData[topItem.year].incomeYTDActualVsBudgetVariance +=
                    filterStructure[i][topItem.year].totalYTDActual -
                    filterStructure[i][topItem.year].totalYTDBudget;
                  profitData[topItem.year].incomeYTDActualVsForecastVariance +=
                    filterStructure[i][topItem.year].totalYTDActual -
                    filterStructure[i][topItem.year].totalYTDForecast;

                  if (filterStructure[i].alreadyIncluded) {
                    alreadyIncluded[topItem.year].Actual +=
                      filterStructure[i][topItem.year].totalActual;
                    alreadyIncluded[topItem.year].Budget +=
                      filterStructure[i][topItem.year].totalBudget;
                    alreadyIncluded[topItem.year].Forecast +=
                      filterStructure[i][topItem.year].totalForecast;
                    alreadyIncluded[topItem.year].ActualVsBudgetVariance +=
                      filterStructure[i][topItem.year].Actual -
                      filterStructure[i][topItem.year].Budget;
                    alreadyIncluded[topItem.year].ActualVsForecastVariance +=
                      filterStructure[i][topItem.year].Actual -
                      filterStructure[i][topItem.year].Forecast;
                    alreadyIncluded[topItem.year].YTDActual +=
                      filterStructure[i][topItem.year].totalYTDActual;
                    alreadyIncluded[topItem.year].YTDBudget +=
                      filterStructure[i][topItem.year].totalYTDBudget;
                    alreadyIncluded[topItem.year].YTDForecast +=
                      filterStructure[i][topItem.year].totalYTDForecast;
                    alreadyIncluded[topItem.year].YTDActualVsBudgetVariance +=
                      filterStructure[i][topItem.year].totalYTDActual -
                      filterStructure[i][topItem.year].totalYTDBudget;
                    alreadyIncluded[topItem.year].YTDActualVsForecastVariance +=
                      filterStructure[i][topItem.year].totalYTDActual -
                      filterStructure[i][topItem.year].totalYTDForecast;
                  }
                } else {
                  profitData[topItem.year].expenseActual +=
                    filterStructure[i][topItem.year].totalActual;
                  profitData[topItem.year].expenseBudget +=
                    filterStructure[i][topItem.year].totalBudget;
                  profitData[topItem.year].expenseForecast +=
                    filterStructure[i][topItem.year].totalForecast;
                  profitData[topItem.year].expenseActualVsBudgetVariance +=
                    filterStructure[i][topItem.year].totalActual -
                    filterStructure[i][topItem.year].totalBudget;
                  profitData[topItem.year].expenseActualVsForecastVariance +=
                    filterStructure[i][topItem.year].totalActual -
                    filterStructure[i][topItem.year].totalForecast;
                  profitData[topItem.year].expenseYTDActual +=
                    filterStructure[i][topItem.year].totalYTDActual;
                  profitData[topItem.year].expenseYTDBudget +=
                    filterStructure[i][topItem.year].totalYTDBudget;
                  profitData[topItem.year].expenseYTDForecast +=
                    filterStructure[i][topItem.year].totalYTDForecast;
                  profitData[topItem.year].expenseYTDActualVsBudgetVariance +=
                    filterStructure[i][topItem.year].totalYTDActual -
                    filterStructure[i][topItem.year].totalYTDBudget;
                  profitData[topItem.year].expenseYTDActualVsForecastVariance +=
                    filterStructure[i][topItem.year].totalYTDActual -
                    filterStructure[i][topItem.year].totalYTDForecast;
                }
              });
            } else {
              for (let j = total_index; j < i; j++) {
                sortedSelectedTopItems.forEach((topItem) => {
                  if (filterStructure[j].income) {
                    filterStructure[i].profit[topItem.year].incomeActual +=
                      filterStructure[j][topItem.year].totalActual;
                    filterStructure[i].profit[topItem.year].incomeBudget +=
                      filterStructure[j][topItem.year].totalBudget;
                    filterStructure[i].profit[topItem.year].incomeForecast +=
                      filterStructure[j][topItem.year].totalForecast;
                    filterStructure[i].profit[
                      topItem.year
                    ].incomeActualVsBudgetVariance +=
                      filterStructure[j][topItem.year].totalActual -
                      filterStructure[j][topItem.year].totalBudget;
                    filterStructure[i].profit[
                      topItem.year
                    ].incomeActualVsForecastVariance +=
                      filterStructure[j][topItem.year].totalActual -
                      filterStructure[j][topItem.year].totalForecast;
                    filterStructure[i].profit[topItem.year].incomeYTDActual +=
                      filterStructure[j][topItem.year].totalYTDActual;
                    filterStructure[i].profit[topItem.year].incomeYTDBudget +=
                      filterStructure[j][topItem.year].totalYTDBudget;
                    filterStructure[i].profit[topItem.year].incomeYTDForecast +=
                      filterStructure[j][topItem.year].totalYTDForecast;
                    filterStructure[i].profit[
                      topItem.year
                    ].incomeYTDActualVsBudgetVariance +=
                      filterStructure[j][topItem.year].totalYTDActual -
                      filterStructure[j][topItem.year].totalYTDBudget;
                    filterStructure[i].profit[
                      topItem.year
                    ].incomeYTDActualVsForecastVariance +=
                      filterStructure[j][topItem.year].totalYTDActual -
                      filterStructure[j][topItem.year].totalYTDForecast;
                  } else {
                    filterStructure[i].profit[topItem.year].expenseActual +=
                      filterStructure[j][topItem.year].totalActual;
                    filterStructure[i].profit[topItem.year].expenseBudget +=
                      filterStructure[j][topItem.year].totalBudget;
                    filterStructure[i].profit[topItem.year].expenseForecast +=
                      filterStructure[j][topItem.year].totalForecast;
                    filterStructure[i].profit[
                      topItem.year
                    ].expenseActualVsBudgetVariance +=
                      filterStructure[j][topItem.year].totalActual -
                      filterStructure[j][topItem.year].totalBudget;
                    filterStructure[i].profit[
                      topItem.year
                    ].expenseActualVsForecastVariance +=
                      filterStructure[j][topItem.year].totalActual -
                      filterStructure[j][topItem.year].totalForecast;
                    filterStructure[i].profit[topItem.year].expenseYTDActual +=
                      filterStructure[j][topItem.year].totalYTDActual;
                    filterStructure[i].profit[topItem.year].expenseYTDBudget +=
                      filterStructure[j][topItem.year].totalYTDBudget;
                    filterStructure[i].profit[
                      topItem.year
                    ].expenseYTDForecast +=
                      filterStructure[j][topItem.year].totalYTDForecast;
                    filterStructure[i].profit[
                      topItem.year
                    ].expenseYTDActualVsBudgetVariance +=
                      filterStructure[j][topItem.year].totalYTDActual -
                      filterStructure[j][topItem.year].totalYTDBudget;
                    filterStructure[i].profit[
                      topItem.year
                    ].expenseYTDActualVsForecastVariance +=
                      filterStructure[j][topItem.year].totalYTDActual -
                      filterStructure[j][topItem.year].totalYTDForecast;
                  }
                });
                sortedSelectedTopItems.forEach((topItem) => {
                  filterStructure[i].pprofit[topItem.year].Actual =
                    filterStructure[i].profit[topItem.year].incomeActual !== 0
                      ? Math.round(
                          ((filterStructure[i].profit[topItem.year]
                            .incomeActual -
                            filterStructure[i].profit[topItem.year]
                              .expenseActual) /
                            filterStructure[i].profit[topItem.year]
                              .incomeActual) *
                            100,
                        )
                      : 0;
                  filterStructure[i].pprofit[topItem.year].Budget =
                    filterStructure[i].profit[topItem.year].incomeBudget !== 0
                      ? Math.round(
                          ((filterStructure[i].profit[topItem.year]
                            .incomeBudget -
                            filterStructure[i].profit[topItem.year]
                              .expenseBudget) /
                            filterStructure[i].profit[topItem.year]
                              .incomeBudget) *
                            100,
                        )
                      : 0;
                  filterStructure[i].pprofit[topItem.year].Forecast =
                    filterStructure[i].profit[topItem.year].incomeForecast !== 0
                      ? Math.round(
                          ((filterStructure[i].profit[topItem.year]
                            .incomeForecast -
                            filterStructure[i].profit[topItem.year]
                              .expenseForecast) /
                            filterStructure[i].profit[topItem.year]
                              .incomeForecast) *
                            100,
                        )
                      : 0;
                  filterStructure[i].pprofit[
                    topItem.year
                  ].ActualVsBudgetVariance =
                    filterStructure[i].pprofit[topItem.year].Actual -
                    filterStructure[i].pprofit[topItem.year].Budget;
                  filterStructure[i].pprofit[
                    topItem.year
                  ].ActualVsForecastVariance =
                    filterStructure[i].pprofit[topItem.year].Actual -
                    filterStructure[i].pprofit[topItem.year].Forecast;
                  filterStructure[i].pprofit[topItem.year].YTDActual =
                    filterStructure[i].profit[topItem.year].incomeYTDActual !==
                    0
                      ? Math.round(
                          ((filterStructure[i].profit[topItem.year]
                            .incomeYTDActual -
                            filterStructure[i].profit[topItem.year]
                              .expenseYTDActual) /
                            filterStructure[i].profit[topItem.year]
                              .incomeYTDActual) *
                            100,
                        )
                      : 0;
                  filterStructure[i].pprofit[topItem.year].YTDBudget =
                    filterStructure[i].profit[topItem.year].incomeYTDBudget !==
                    0
                      ? Math.round(
                          ((filterStructure[i].profit[topItem.year]
                            .incomeYTDBudget -
                            filterStructure[i].profit[topItem.year]
                              .expenseYTDBudget) /
                            filterStructure[i].profit[topItem.year]
                              .incomeYTDBudget) *
                            100,
                        )
                      : 0;
                  filterStructure[i].pprofit[topItem.year].YTDForecast =
                    filterStructure[i].profit[topItem.year]
                      .incomeYTDForecast !== 0
                      ? Math.round(
                          ((filterStructure[i].profit[topItem.year]
                            .incomeYTDForecast -
                            filterStructure[i].profit[topItem.year]
                              .expenseYTDForecast) /
                            filterStructure[i].profit[topItem.year]
                              .incomeYTDForecast) *
                            100,
                        )
                      : 0;
                  filterStructure[i].pprofit[
                    topItem.year
                  ].YTDActualVsBudgetVariance =
                    filterStructure[i].pprofit[topItem.year].YTDActual -
                    filterStructure[i].pprofit[topItem.year].YTDBudget;
                  filterStructure[i].pprofit[
                    topItem.year
                  ].YTDActualVsForecastVariance =
                    filterStructure[i].pprofit[topItem.year].YTDActual -
                    filterStructure[i].pprofit[topItem.year].YTDForecast;
                });
                total_index = i + 1;
              }
            }
          }

          sortedSelectedTopItems.forEach((topItem) => {
            pprofitData[topItem.year] = {};
            pprofitData[topItem.year].Actual =
              profitData[topItem.year].incomeActual !== 0
                ? Math.round(
                    ((profitData[topItem.year].incomeActual -
                      profitData[topItem.year].expenseActual) /
                      (profitData[topItem.year].incomeActual -
                        alreadyIncluded[topItem.year].Actual)) *
                      100,
                  )
                : 0;
            pprofitData[topItem.year].Budget =
              profitData[topItem.year].incomeBudget !== 0
                ? Math.round(
                    ((profitData[topItem.year].incomeBudget -
                      profitData[topItem.year].expenseBudget) /
                      (profitData[topItem.year].incomeBudget -
                        alreadyIncluded[topItem.year].Budget)) *
                      100,
                  )
                : 0;
            pprofitData[topItem.year].Forecast =
              profitData[topItem.year].incomeForecast !== 0
                ? Math.round(
                    ((profitData[topItem.year].incomeForecast -
                      profitData[topItem.year].expenseForecast) /
                      (profitData[topItem.year].incomeForecast -
                        alreadyIncluded[topItem.year].Forecast)) *
                      100,
                  )
                : 0;
            pprofitData[topItem.year].ActualVsBudgetVariance =
              pprofitData[topItem.year].Actual - pprofitData[topItem.year].Budget;
            pprofitData[topItem.year].ActualVsForecastVariance =
              pprofitData[topItem.year].Actual - pprofitData[topItem.year].Forecast;
            pprofitData[topItem.year].YTDActual =
              profitData[topItem.year].incomeYTDActual !== 0
                ? Math.round(
                    ((profitData[topItem.year].incomeYTDActual -
                      profitData[topItem.year].expenseYTDActual) /
                      (profitData[topItem.year].incomeYTDActual -
                        alreadyIncluded[topItem.year].YTDActual)) *
                      100,
                  )
                : 0;
            pprofitData[topItem.year].YTDBudget =
              profitData[topItem.year].incomeYTDBudget !== 0
                ? Math.round(
                    ((profitData[topItem.year].incomeYTDBudget -
                      profitData[topItem.year].expenseYTDBudget) /
                      (profitData[topItem.year].incomeYTDBudget -
                        alreadyIncluded[topItem.year].YTDBudget)) *
                      100,
                  )
                : 0;
            pprofitData[topItem.year].YTDForecast =
              profitData[topItem.year].incomeYTDForecast !== 0
                ? Math.round(
                    ((profitData[topItem.year].incomeYTDForecast -
                      profitData[topItem.year].expenseYTDForecast) /
                      (profitData[topItem.year].incomeYTDForecast -
                        alreadyIncluded[topItem.year].YTDForecast)) *
                      100,
                  )
                : 0;
            pprofitData[topItem.year].YTDActualVsBudgetVariance =
              pprofitData[topItem.year].YTDActual - pprofitData[topItem.year].YTDBudget;
            pprofitData[topItem.year].YTDActualVsForecastVariance =
              pprofitData[topItem.year].YTDActual -
              pprofitData[topItem.year].YTDForecast;
          });
        }

        setFilterStructure(filterStructure);
        setProfit(profitData);
        setPprofit(pprofitData);
      }
    }
    deSelectAll();
  };

  const handleElement = (event, element) => {
    let _selectedIdRects = [];
    let exist = false,
      index = NaN;

    switch (element) {
      case 'table':
        if (event.shiftKey) {
          _selectedIdRects = selectedIdRects.slice();

          for (let i = 0; i < selectedIdRects.length; i++) {
            if (selectedIdRects[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('bkgActive');
            _selectedIdRects.push(event.target);
          } else {
            event.target.classList.remove('bkgActive');
            _selectedIdRects.splice(index, 1);
          }
        } else {
          selectedIdRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
            if (rect === event.target) exist = true;
          });

          if (exist && selectedIdRects.length === 1) {
            _selectedIdRects = [];
          } else {
            event.target.classList.add('bkgActive');

            _selectedIdRects = [event.target];
          }
        }
        break;

      default:
        break;
    }

    setSelectedIdRects(_selectedIdRects);
  };

  const deSelectAll = () => {
    selectedIdRects.forEach((rect) => {
      rect.classList.remove('bkgActive');
    });

    setSelectedIdRects([]);
  };

  const showTooltipHandler = (event, value, year, category) => {
    let tooltipData = {};
    let top, left;

    top = event.pageY - pageOffset + 150;
    left =
      (window.innerWidth * 2) / 3 < event.clientX
        ? event.clientX - 300
        : event.clientX + 20;

    tooltipData['data'] = value;
    tooltipData['FY'] = year;
    tooltipData['IncomeStatementCategory'] = category;

    if (category && category.toLowerCase().includes('profit'))
      tooltipData['disp'] = 'percent';
    else tooltipData['disp'] = '';

    showTooltip({
      tooltipData: tooltipData,
      tooltipTop: top,
      tooltipLeft: left,
    });
  };

  const handlePos = (event) => {
    const _pos =
      event.currentTarget.getBoundingClientRect().top + window.scrollY;
    if (pageOffset !== _pos && isMountedRef.current === true) {
      setPageOffset(_pos);
    }
  };

  const hideTooltipHandler = () => {
    if (hoverBar) hoverBar.classList.remove('barHover');
    setHoverBar(null);

    hideTooltip();
  };

  const handleChangeShowPercent = (event) => {
    handleFilter({
      showPercent: event.target.checked,
    });
  };

  const handleChange = (event) => {
    handleFilter({ selectedOption: event.target.value });
  };

  const handleChangeProfitName = (event, type) => {
    if (type === 'profit')
      setProfitName(event.target.value);
    else
      setPprofitName(event.target.value);
  };

  const handleSubmitProfitName = (type) => {
    if (type === 'profit')
      handleFilter({
        profitName: profitName,
      });
    else
      handleFilter({
        pprofitName: pprofitName,
      });
  };

  let offset = 0;
  if (document.getElementById('yfp_bottom')) {
    offset = document
      .getElementById('yfp_bottom')
      .getBoundingClientRect().top;
    if (offset === null) offset = 0;
  }

  const width = window.innerWidth;
  const height = window.innerHeight - offset - 60;
  const margin = {
    top: 0,
    right: 10,
    bottom: 0,
    left: 10,
  };

  const { pageYOffset, yMax } = getParams(window, width, height, margin);
  let tooltipTimeout;

  let selectedYearLength = selectedYears.length;
  selectedYearLength =
    selectedYearLength === 7 ? selectedYearLength : selectedYearLength + 1;

  return (
    <div
      style={{
        marginTop: 10,
      }}
      onMouseMove={(event) => handlePos(event)}
      onTouchMove={(event) => handlePos(event)}
    >
      {filterStructure.length > 0 ? (
        <div
          id="yfp_bottom"
          style={{
            overflowY: 'auto',
            maxHeight: `${yMax}px`,
            fontSize: 12,
          }}
        >
          {/* year */}
          <Grid container>
            <div
              style={{
                height: 20,
                width:
                  selectedYearLength === 0
                    ? (window.innerWidth - 20) / 2
                    : (window.innerWidth - 20) / selectedYearLength,
                position: 'relative',
              }}
            >
              <FormControlLabel
                control={
                  <FormControl variant="standard">
                    <Select
                      onChange={(event) => handleChange(event)}
                      sx={{
                        '& .MuiInputBase-input': {
                          padding: 0,
                          paddingRight: 24,
                          fontWeight: 'bold',
                          fontFamily: 'Verdana !important',
                          fontSize: '10pt',
                        },
                      }}
                      value={selectedOption}
                    >
                      <MenuItem value="Forecast">Forecast</MenuItem>
                      <MenuItem value="Budget">Budget</MenuItem>
                    </Select>
                  </FormControl>
                }
                label="Profit / Loss vs&nbsp;"
                labelPlacement="start"
                sx={{
                  marginLeft: '0px !important',
                  '& .MuiTypography-root': {
                    fontWeight: 'bold',
                    fontFamily: 'Verdana !important',
                    fontSize: '10pt',
                  },
                }}
              />
              <FormControlLabel
                id="yfp_percent"
                control={
                  <Checkbox
                    checked={showPercent}
                    onChange={handleChangeShowPercent}
                    color="success"
                  />
                }
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 200,
                  width: 90,
                  '& .MuiCheckbox-root': {
                    padding: 0,
                  },
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 'bold',
                    fontFamily: 'Verdana !important',
                    fontSize: '10pt',
                  },
                  '& .MuiCheckbox-colorPrimary': {
                    color: '#1976d2',
                  },
                }}
                label="Show %"
                labelPlacement="start"
              />
            </div>
            {selectedItems.map((item, index) => (
              <div
                key={index}
                style={{
                  height: 20,
                  width:
                    selectedYearLength === 0
                      ? 'calc(50% - 8px)'
                      : index === selectedItems.length -1 ? 'auto' : `calc(${100 / selectedYearLength}% - 0px)`,
                  flex: index === selectedItems.length -1 ? 'auto' : '',
                  textAlign: 'center',
                }}
              >
                <b>{item.year}</b>
              </div>
            ))}
          </Grid>

          {/* header */}
          <Grid container>
            <div
              style={{
                borderBottom: '1px solid lightgrey',
                height: 24,
                textAlign: 'center',
                width:
                  selectedYearLength === 0
                    ? 'calc(50% - 8px)'
                    : `calc(${100 / selectedYearLength}% - 0px)`,
              }}
            >
            </div>
            {selectedItems.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  width:
                    selectedYearLength === 0
                      ? 'calc(50% - 8px)'
                      : `calc(${100 / selectedYearLength}% - 0px)`,
                }}
              >
                <Grid
                  size={showPercent ? 3 : 6}
                  style={{
                    borderBottom: '1px solid lightgrey',
                    height: 24,
                    textAlign: 'center',
                  }}
                >
                  <b>Actual</b>
                </Grid>
                {showPercent ? (
                  <Grid
                    size={3}
                    style={{
                      borderBottom: '1px solid lightgrey',
                      height: 24,
                      textAlign: 'center',
                    }}
                  >
                    <b>%</b>
                  </Grid>
                ) : null}
                <Grid
                  size={showPercent ? 3 : 6}
                  style={{
                    borderBottom: '1px solid lightgrey',
                    height: 24,
                    textAlign: 'center',
                  }}
                >
                  <b>
                    {selectedOption === 'Forecast'
                      ? showPercent
                        ? 'Forecast'
                        : 'YTD Forecast'
                      : showPercent
                      ? 'Budget'
                      : 'YTD Budget'}
                  </b>
                </Grid>
                {showPercent ? (
                  <Grid
                    size={3}
                    style={{
                      borderBottom: '1px solid lightgrey',
                      height: 24,
                      textAlign: 'center',
                    }}
                  >
                    <b>%</b>
                  </Grid>
                ) : null}
              </div>
            ))}
          </Grid>

          {/* account */}
          {filterStructure.map((parent, pIndex) => {
            if (parent.total) {
              return (
                <React.Fragment key={pIndex}>
                  {parent.subItems.map((child, cIndex) => (
                    <Grid container key={cIndex}>
                      <div
                        style={{
                          display: 'flex',
                          width:
                            selectedYearLength === 0
                              ? 'calc(50% - 8px)'
                              : `calc(${100 / selectedYearLength}% - 0px)`,
                        }}
                      >
                        <Grid
                          size={5}
                          style={{
                            ...tableBorder,
                            borderBottom: 'none',
                            justifyContent: 'start',
                            borderRight: 'unset',
                          }}
                        >
                          <b>{cIndex === 0 ? parent.content : ''}</b>
                        </Grid>
                        <Grid
                          size={7}
                          style={{
                            ...tableBorder,
                            justifyContent: 'start',
                            backgroundColor:
                              parent.subItems.length % 2 === 0
                                ? cIndex % 2 === 0
                                  ? '#f7f7f7'
                                  : '#ffffff'
                                : cIndex % 2 === 1
                                ? '#f7f7f7'
                                : '#ffffff',
                          }}
                        >
                          {child.content}
                        </Grid>
                      </div>
                      {selectedItems.map((item, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            width:
                              selectedYearLength === 0
                                ? 'calc(50% - 8px)'
                                : `calc(${100 / selectedYearLength}% - 0px)`,
                          }}
                        >
                          <Grid
                            size={showPercent ? 3 : 6}
                            style={{
                              ...tableBorder,
                              backgroundColor:
                                parent.subItems.length % 2 === 0
                                  ? cIndex % 2 === 0
                                    ? index % 2 === 0
                                      ? '#f7f7f7'
                                      : '#f0f0f0'
                                    : '#ffffff'
                                  : cIndex % 2 === 1
                                  ? index % 2 === 1
                                    ? '#f0f0f0'
                                    : '#f7f7f7'
                                  : '#ffffff',
                            }}
                            onClick={(event) => {
                              handleElement(event, 'table');
                            }}
                            onMouseMove={(event) => {
                              if (tooltipTimeout)
                                clearTimeout(tooltipTimeout);
                              showTooltipHandler(
                                event,
                                child[item.year].YTDActual,
                                item.year,
                                child.content,
                              );
                            }}
                            onMouseLeave={(event) => {
                              tooltipTimeout = setTimeout(() => {
                                hideTooltipHandler();
                              }, 300);
                            }}
                            onTouchMove={(event) => {
                              if (tooltipTimeout)
                                clearTimeout(tooltipTimeout);
                              showTooltipHandler(
                                event,
                                child[item.year].YTDActual,
                                item.year,
                                child.content,
                              );
                            }}
                            onTouchEnd={(event) => {
                              tooltipTimeout = setTimeout(() => {
                                hideTooltipHandler();
                              }, 300);
                            }}
                          >
                            {child[item.year]?.YTDActual >= 0
                              ? thousandFormat2(child[item.year]?.YTDActual)
                              : `(${thousandFormat2(
                                  Math.abs(child[item.year]?.YTDActual),
                                )})`}
                          </Grid>
                          {showPercent ? (
                            <Grid
                              size={3}
                              style={{
                                ...tableBorder,
                                backgroundColor:
                                  parent.subItems.length % 2 === 0
                                    ? cIndex % 2 === 0
                                      ? index % 2 === 0
                                        ? '#f7f7f7'
                                        : '#f0f0f0'
                                      : '#ffffff'
                                    : cIndex % 2 === 1
                                    ? index % 2 === 1
                                      ? '#f0f0f0'
                                      : '#f7f7f7'
                                    : '#ffffff',
                              }}
                            >
                              {profit[item.year].incomeActual === 0
                                ? '0%'
                                : Math.round(
                                    (child[item.year].Actual /
                                      profit[item.year].incomeActual) *
                                      100,
                                  ) >= 0
                                ? `${Math.round(
                                    (child[item.year].Actual /
                                      profit[item.year].incomeActual) *
                                      100,
                                  )}%`
                                : `(${Math.round(
                                    Math.abs(
                                      (child[item.year].Actual /
                                        profit[item.year].incomeActual) *
                                        100,
                                    ),
                                  )}%)`}
                            </Grid>
                          ) : null}
                          <Grid
                            size={showPercent ? 3 : 6}
                            style={{
                              ...tableBorder,
                              backgroundColor:
                                parent.subItems.length % 2 === 0
                                  ? cIndex % 2 === 0
                                    ? index % 2 === 0
                                      ? '#f7f7f7'
                                      : '#f0f0f0'
                                    : '#ffffff'
                                  : cIndex % 2 === 1
                                  ? index % 2 === 1
                                    ? '#f0f0f0'
                                    : '#f7f7f7'
                                  : '#ffffff',
                            }}
                            onClick={(event) => {
                              handleElement(event, 'table');
                            }}
                            onMouseMove={(event) => {
                              if (tooltipTimeout)
                                clearTimeout(tooltipTimeout);
                              showTooltipHandler(
                                event,
                                selectedOption === 'Forecast'
                                  ? child[item.year].YTDForecast
                                  : child[item.year].YTDBudget,
                                item.year,
                                child.content,
                              );
                            }}
                            onMouseLeave={(event) => {
                              tooltipTimeout = setTimeout(() => {
                                hideTooltipHandler();
                              }, 300);
                            }}
                            onTouchMove={(event) => {
                              if (tooltipTimeout)
                                clearTimeout(tooltipTimeout);
                              showTooltipHandler(
                                event,
                                selectedOption === 'Forecast'
                                  ? child[item.year].YTDForecast
                                  : child[item.year].YTDBudget,
                                item.year,
                                child.content,
                              );
                            }}
                            onTouchEnd={(event) => {
                              tooltipTimeout = setTimeout(() => {
                                hideTooltipHandler();
                              }, 300);
                            }}
                          >
                            {selectedOption === 'Forecast'
                              ? child[item.year]?.YTDForecast >= 0
                                ? thousandFormat2(
                                    child[item.year]?.YTDForecast,
                                  )
                                : `(${thousandFormat2(
                                    Math.abs(child[item.year]?.YTDForecast),
                                  )})`
                              : child[item.year]?.YTDBudget >= 0
                              ? thousandFormat2(child[item.year]?.YTDBudget)
                              : `(${thousandFormat2(
                                  Math.abs(child[item.year]?.YTDBudget),
                                )})`}
                          </Grid>
                          {showPercent ? (
                            <Grid
                              size={3}
                              style={{
                                ...tableBorder,
                                backgroundColor:
                                  parent.subItems.length % 2 === 0
                                    ? cIndex % 2 === 0
                                      ? index % 2 === 0
                                        ? '#f7f7f7'
                                        : '#f0f0f0'
                                      : '#ffffff'
                                    : cIndex % 2 === 1
                                    ? index % 2 === 1
                                      ? '#f0f0f0'
                                      : '#f7f7f7'
                                    : '#ffffff',
                              }}
                            >
                              {selectedOption === 'Forecast'
                                ? profit[item.year].incomeForecast === 0
                                  ? '0%'
                                  : Math.round(
                                      (child[item.year].Forecast /
                                        profit[item.year].incomeForecast) *
                                        100,
                                    ) >= 0
                                  ? `${Math.round(
                                      (child[item.year].Forecast /
                                        profit[item.year].incomeForecast) *
                                        100,
                                    )}%`
                                  : `(${Math.round(
                                      Math.abs(
                                        (child[item.year].Forecast /
                                          profit[item.year].incomeForecast) *
                                          100,
                                      ),
                                    )}%)`
                                : profit[item.year].incomeBudget === 0
                                ? '0%'
                                : Math.round(
                                    (child[item.year].Budget /
                                      profit[item.year].incomeBudget) *
                                      100,
                                  ) >= 0
                                ? `${Math.round(
                                    (child[item.year].Budget /
                                      profit[item.year].incomeBudget) *
                                      100,
                                  )}%`
                                : `(${Math.round(
                                    Math.abs(
                                      (child[item.year].Budget /
                                        profit[item.year].incomeBudget) *
                                        100,
                                    ),
                                  )}%)`}
                            </Grid>
                          ) : null}
                        </div>
                      ))}
                    </Grid>
                  ))}
                  <Grid container>
                    <div
                      style={{
                        display: 'flex',
                        width:
                          selectedYearLength === 0
                            ? 'calc(50% - 8px)'
                            : `calc(${100 / selectedYearLength}% - 0px)`,
                      }}
                    >
                      <Grid size={5}></Grid>
                      <Grid
                        size={7}
                        style={{
                          ...tableBorder,
                          justifyContent: 'start',
                          backgroundColor: '#f7f7f7',
                        }}
                      >
                        <b>{parent.totalTitle}</b>
                      </Grid>
                    </div>
                    {selectedItems.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          width:
                            selectedYearLength === 0
                              ? 'calc(50% - 8px)'
                              : `calc(${100 / selectedYearLength}% - 0px)`,
                        }}
                      >
                        <Grid
                          size={showPercent ? 3 : 6}
                          style={{
                            ...tableBorder,
                            backgroundColor:
                              index % 2 === 0 ? '#f7f7f7' : '#f0f0f0',
                          }}
                          onClick={(event) => {
                            handleElement(event, 'table');
                          }}
                          onMouseMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(
                              event,
                              parent[item.year].totalYTDActual,
                              item.year,
                              parent.totalTitle,
                            );
                          }}
                          onMouseLeave={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                          onTouchMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(
                              event,
                              parent[item.year].totalYTDActual,
                              item.year,
                              parent.totalTitle,
                            );
                          }}
                          onTouchEnd={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                        >
                          <b>
                            {parent[item.year]?.totalYTDActual >= 0
                              ? thousandFormat2(
                                  parent[item.year]?.totalYTDActual,
                                )
                              : `(${thousandFormat2(
                                  Math.abs(parent[item.year]?.totalYTDActual),
                                )})`}
                          </b>
                        </Grid>
                        {showPercent ? (
                          <Grid
                            size={3}
                            style={{
                              ...tableBorder,
                              backgroundColor:
                                index % 2 === 0 ? '#f7f7f7' : '#f0f0f0',
                            }}
                          >
                            <b>
                              {profit[item.year].incomeActual === 0
                                ? '0%'
                                : Math.round(
                                    (parent[item.year].totalActual /
                                      profit[item.year].incomeActual) *
                                      100,
                                  ) >= 0
                                ? `${Math.round(
                                    (parent[item.year].totalActual /
                                      profit[item.year].incomeActual) *
                                      100,
                                  )}%`
                                : `(${Math.round(
                                    Math.abs(
                                      (parent[item.year].totalActual /
                                        profit[item.year].incomeActual) *
                                        100,
                                    ),
                                  )}%)`}
                            </b>
                          </Grid>
                        ) : null}
                        <Grid
                          size={showPercent ? 3 : 6}
                          style={{
                            ...tableBorder,
                            backgroundColor:
                              index % 2 === 0 ? '#f7f7f7' : '#f0f0f0',
                          }}
                          onClick={(event) => {
                            handleElement(event, 'table');
                          }}
                          onMouseMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(
                              event,
                              selectedOption === 'Forecast'
                                ? parent[item.year].totalYTDForecast
                                : parent[item.year].totalYTDBudget,
                              item.year,
                              parent.totalTitle,
                            );
                          }}
                          onMouseLeave={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                          onTouchMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(
                              event,
                              selectedOption === 'Forecast'
                                ? parent[item.year].totalYTDForecast
                                : parent[item.year].totalYTDBudget,
                              item.year,
                              parent.totalTitle,
                            );
                          }}
                          onTouchEnd={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                        >
                          <b>
                            {selectedOption === 'Forecast'
                              ? parent[item.year]?.totalYTDForecast >= 0
                                ? thousandFormat2(
                                    parent[item.year].totalYTDForecast,
                                  )
                                : `(${thousandFormat2(
                                    Math.abs(
                                      parent[item.year]?.totalYTDForecast,
                                    ),
                                  )})`
                              : parent[item.year]?.totalYTDBudget >= 0
                              ? thousandFormat2(
                                  parent[item.year].totalYTDBudget,
                                )
                              : `(${thousandFormat2(
                                  Math.abs(parent[item.year]?.totalYTDBudget),
                                )})`}
                          </b>
                        </Grid>
                        {showPercent ? (
                          <Grid
                            size={3}
                            style={{
                              ...tableBorder,
                              backgroundColor:
                                index % 2 === 0 ? '#f7f7f7' : '#f0f0f0',
                            }}
                          >
                            <b>
                              {selectedOption === 'Forecast'
                                ? profit[item.year].incomeForecast === 0
                                  ? '0%'
                                  : Math.round(
                                      (parent[item.year].totalForecast /
                                        profit[item.year].incomeForecast) *
                                        100,
                                    ) >= 0
                                  ? `${Math.round(
                                      (parent[item.year].totalForecast /
                                        profit[item.year].incomeForecast) *
                                        100,
                                    )}%`
                                  : `(${Math.round(
                                      Math.abs(
                                        (parent[item.year].totalForecast /
                                          profit[item.year].incomeForecast) *
                                            100,
                                      ),
                                    )}%)`
                                : profit[item.year].incomeBudget === 0
                                ? '0%'
                                : Math.round(
                                    (parent[item.year].totalBudget /
                                      profit[item.year].incomeBudget) *
                                      100,
                                  ) >= 0
                                ? `${Math.round(
                                    (parent[item.year].totalBudget /
                                      profit[item.year].incomeBudget) *
                                      100,
                                  )}%`
                                : `(${Math.round(
                                    Math.abs(
                                      (parent[item.year].totalBudget /
                                        profit[item.year].incomeBudget) *
                                        100,
                                    ),
                                  )}%)`}
                            </b>
                          </Grid>
                        ) : null}
                      </div>
                    ))}
                  </Grid>
                </React.Fragment>
              );
            } else {
              return (
                <React.Fragment key={pIndex}>
                  <Grid container>
                    <div
                      style={{
                        display: 'flex',
                        width:
                          selectedYearLength === 0
                            ? 'calc(50% - 8px)'
                            : `calc(${100 / selectedYearLength}% - 0px)`,
                      }}
                    >
                      <Grid
                        size={5}
                        style={{
                          ...tableBorder,
                          borderBottom: 'none',
                          justifyContent: 'start',
                          borderRight: 'unset',
                        }}
                      >
                        <b>{parent.content}</b>
                      </Grid>
                      <Grid
                        size={7}
                        style={{
                          ...tableBorder,
                          justifyContent: 'start',
                        }}
                      >
                        <b>Profit / Loss</b>
                      </Grid>
                    </div>
                    {selectedItems.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          width:
                            selectedYearLength === 0
                              ? 'calc(50% - 8px)'
                              : `calc(${100 / selectedYearLength}% - 0px)`,
                        }}
                      >
                        <Grid
                          size={6}
                          style={tableBorder}
                          onClick={(event) => {
                            handleElement(event, 'table');
                          }}
                          onMouseMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(
                              event,
                              parent.profit[item.year].incomeYTDActual -
                                parent.profit[item.year].expenseYTDActual,
                              item.year,
                              'Profit/Loss',
                            );
                          }}
                          onMouseLeave={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                          onTouchMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(
                              event,
                              parent.profit[item.year].incomeYTDActual -
                                parent.profit[item.year].expenseYTDActual,
                              item.year,
                              'Profit/Loss',
                            );
                          }}
                          onTouchEnd={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                        >
                          <b>
                            {parent.profit[item.year]?.incomeYTDActual -
                              parent.profit[item.year]?.expenseYTDActual >=
                            0
                              ? thousandFormat2(
                                  parent.profit[item.year].incomeYTDActual -
                                    parent.profit[item.year].expenseYTDActual,
                                )
                              : `(${thousandFormat2(
                                  Math.abs(
                                    parent.profit[item.year].incomeYTDActual -
                                      parent.profit[item.year]
                                        .expenseYTDActual,
                                  ),
                                )})`}
                          </b>
                        </Grid>
                        <Grid
                          size={6}
                          style={tableBorder}
                          onClick={(event) => {
                            handleElement(event, 'table');
                          }}
                          onMouseMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(
                              event,
                              selectedOption === 'Forecast'
                                ? parent.profit[item.year].incomeYTDForecast -
                                    parent.profit[item.year]
                                      .expenseYTDForecast
                                : parent.profit[item.year].incomeYTDBudget -
                                    parent.profit[item.year].expenseYTDBudget,
                              item.year,
                              'Profit/Loss',
                            );
                          }}
                          onMouseLeave={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                          onTouchMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(
                              event,
                              selectedOption === 'Forecast'
                                ? parent.profit[item.year].incomeYTDForecast -
                                    parent.profit[item.year]
                                      .expenseYTDForecast
                                : parent.profit[item.year].incomeYTDBudget -
                                    parent.profit[item.year].expenseYTDBudget,
                              item.year,
                              'Profit/Loss',
                            );
                          }}
                          onTouchEnd={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                        >
                          <b>
                            {selectedOption === 'Forecast'
                              ? parent.profit[item.year]?.incomeYTDForecast -
                                  parent.profit[item.year]?.expenseYTDForecast >=
                                0
                                ? thousandFormat2(
                                    parent.profit[item.year]
                                      .incomeYTDForecast -
                                      parent.profit[item.year]
                                        .expenseYTDForecast,
                                  )
                                : `(${thousandFormat2(
                                    Math.abs(
                                      parent.profit[item.year]
                                        .incomeYTDForecast -
                                        parent.profit[item.year]
                                          .expenseYTDForecast,
                                    ),
                                  )})`
                              : parent.profit[item.year].incomeYTDBudget -
                                  parent.profit[item.year].expenseYTDBudget >=
                                0
                              ? thousandFormat2(
                                  parent.profit[item.year].incomeYTDBudget -
                                    parent.profit[item.year].expenseYTDBudget,
                                )
                              : `(${thousandFormat2(
                                  Math.abs(
                                    parent.profit[item.year].incomeYTDBudget -
                                      parent.profit[item.year]
                                        .expenseYTDBudget,
                                  ),
                                )})`}
                          </b>
                        </Grid>
                      </div>
                    ))}
                  </Grid>

                  <Grid container>
                    <div
                      style={{
                        display: 'flex',
                        width:
                          selectedYearLength === 0
                            ? 'calc(50% - 8px)'
                            : `calc(${100 / selectedYearLength}% - 0px)`,
                      }}
                    >
                      <Grid
                        size={5}
                        style={{
                          ...tableBorder,
                          borderBottom: 'none',
                          justifyContent: 'start',
                          borderRight: 'unset',
                        }}
                      >
                        <b>% {parent.content}</b>
                      </Grid>
                      <Grid
                        size={7}
                        style={{
                          ...tableBorder,
                          justifyContent: 'start',
                        }}
                      >
                        <b>% Profit / Loss</b>
                      </Grid>
                    </div>
                    {selectedItems.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          width:
                            selectedYearLength === 0
                              ? 'calc(50% - 8px)'
                              : `calc(${100 / selectedYearLength}% - 0px)`,
                        }}
                      >
                        <Grid
                          size={6}
                          style={{
                            ...tableBorder,
                            backgroundColor:
                              index % 2 === 0 ? '#f7f7f7' : '#f0f0f0',
                          }}
                          onClick={(event) => {
                            handleElement(event, 'table');
                          }}
                          onMouseMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(
                              event,
                              parent.pprofit[item.year].YTDActual,
                              item.year,
                              '% Profit',
                            );
                          }}
                          onMouseLeave={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                          onTouchMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(
                              event,
                              parent.pprofit[item.year].YTDActual,
                              item.year,
                              '% Profit',
                            );
                          }}
                          onTouchEnd={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                        >
                          <b>
                            {parent.pprofit[item.year]?.YTDActual >= 0
                              ? `${parent.pprofit[item.year]?.YTDActual}%`
                              : `(${Math.abs(
                                  parent.pprofit[item.year]?.YTDActual,
                                )}%)`}
                          </b>
                        </Grid>
                        <Grid
                          size={6}
                          style={{
                            ...tableBorder,
                            backgroundColor:
                              index % 2 === 0 ? '#f7f7f7' : '#f0f0f0',
                          }}
                          onClick={(event) => {
                            handleElement(event, 'table');
                          }}
                          onMouseMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(
                              event,
                              selectedOption === 'Forecast'
                                ? parent.pprofit[item.year].YTDForecast
                                : parent.pprofit[item.year].YTDBudget,
                              item.year,
                              '% Profit',
                            );
                          }}
                          onMouseLeave={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                          onTouchMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(
                              event,
                              selectedOption === 'Forecast'
                                ? parent.pprofit[item.year].YTDForecast
                                : parent.pprofit[item.year].YTDBudget,
                              item.year,
                              '% Profit',
                            );
                          }}
                          onTouchEnd={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                        >
                          <b>
                            {selectedOption === 'Forecast'
                              ? parent.pprofit[item.year]?.YTDForecast >= 0
                                ? `${parent.pprofit[item.year].YTDForecast}%`
                                : `(${Math.abs(
                                    parent.pprofit[item.year]?.YTDForecast,
                                  )}%)`
                              : parent.pprofit[item.year]?.YTDBudget >= 0
                              ? `${parent.pprofit[item.year].YTDBudget}%`
                              : `(${Math.abs(
                                    parent.pprofit[item.year]?.YTDBudget,
                                  )}%)`}
                          </b>
                        </Grid>
                      </div>
                    ))}
                  </Grid>
                </React.Fragment>
              );
            }
          })}

          <Grid container style={{ height: 10 }}></Grid>

          {/* profit */}
          <Grid container>
            <div
              style={{
                ...tableBorder,
                borderTop: '1px solid lightgrey',
                borderRight: 'none',
                display: 'flex',
                width:
                  selectedYearLength === 0
                    ? 'calc(50% - 8px)'
                    : `calc(${100 / selectedYearLength}% - 0px)`,
                justifyContent: 'start',
              }}
            >
              <TextField
                sx={{
                  height: 25,
                  '& .MuiInputBase-input': {
                    height: 25,
                    padding: 0,
                    fontWeight: 'bold',
                    fontSize: 12,
                    fontFamily: 'Verdana !important',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderLeft: 'none',
                  },
                }}
                value={profitName}
                onChange={(e) => handleChangeProfitName(e, 'profit')}
                onBlur={() => handleSubmitProfitName('profit')}
                fullWidth
              />
            </div>
            {selectedItems.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  borderTop: '1px solid lightgrey',
                  width:
                    selectedYearLength === 0
                      ? 'calc(50% - 8px)'
                      : `calc(${100 / selectedYearLength}% - 0px)`,
                }}
              >
                <Grid
                  size={6}
                  style={tableBorder}
                  onClick={(event) => {
                    handleElement(event, 'table');
                  }}
                  onMouseMove={(event) => {
                    if (tooltipTimeout) clearTimeout(tooltipTimeout);
                    showTooltipHandler(
                      event,
                      profit[item.year].incomeYTDActual -
                        profit[item.year].expenseYTDActual,
                      item.year,
                      'Profit/Loss',
                    );
                  }}
                  onMouseLeave={(event) => {
                    tooltipTimeout = setTimeout(() => {
                      hideTooltipHandler();
                    }, 300);
                  }}
                  onTouchMove={(event) => {
                    if (tooltipTimeout) clearTimeout(tooltipTimeout);
                    showTooltipHandler(
                      event,
                      profit[item.year].incomeYTDActual -
                        profit[item.year].expenseYTDActual,
                      item.year,
                      'Profit/Loss',
                    );
                  }}
                  onTouchEnd={(event) => {
                    tooltipTimeout = setTimeout(() => {
                      hideTooltipHandler();
                    }, 300);
                  }}
                >
                  <b>
                    {(profit[item.year]?.incomeYTDActual || 0) -
                      (profit[item.year]?.expenseYTDActual || 0) >=
                    0
                      ? thousandFormat2(
                          (profit[item.year]?.incomeYTDActual || 0) -
                            (profit[item.year]?.expenseYTDActual || 0),
                        )
                      : `(${thousandFormat2(
                          Math.abs(
                            (profit[item.year]?.incomeYTDActual || 0) -
                              (profit[item.year]?.expenseYTDActual || 0),
                          ),
                        )})`}
                  </b>
                </Grid>
                <Grid
                  size={6}
                  style={tableBorder}
                  onClick={(event) => {
                    handleElement(event, 'table');
                  }}
                  onMouseMove={(event) => {
                    if (tooltipTimeout) clearTimeout(tooltipTimeout);
                    showTooltipHandler(
                      event,
                      selectedOption === 'Forecast'
                        ? profit[item.year].incomeYTDForecast -
                            profit[item.year].expenseYTDForecast
                        : profit[item.year].incomeYTDBudget -
                            profit[item.year].expenseYTDBudget,
                      item.year,
                      'Profit/Loss',
                    );
                  }}
                  onMouseLeave={(event) => {
                    tooltipTimeout = setTimeout(() => {
                      hideTooltipHandler();
                    }, 300);
                  }}
                  onTouchMove={(event) => {
                    if (tooltipTimeout) clearTimeout(tooltipTimeout);
                    showTooltipHandler(
                      event,
                      selectedOption === 'Forecast'
                        ? profit[item.year].incomeYTDForecast -
                            profit[item.year].expenseYTDForecast
                        : profit[item.year].incomeYTDBudget -
                            profit[item.year].expenseYTDBudget,
                      item.year,
                      'Profit/Loss',
                    );
                  }}
                  onTouchEnd={(event) => {
                    tooltipTimeout = setTimeout(() => {
                      hideTooltipHandler();
                    }, 300);
                  }}
                >
                  <b>
                    {selectedOption === 'Forecast'
                      ? (profit[item.year]?.incomeYTDForecast || 0) -
                          (profit[item.year]?.expenseYTDForecast || 0) >=
                        0
                        ? thousandFormat2(
                            (profit[item.year]?.incomeYTDForecast || 0) -
                              (profit[item.year]?.expenseYTDForecast || 0),
                          )
                        : `(${thousandFormat2(
                            Math.abs(
                              (profit[item.year]?.incomeYTDForecast || 0) -
                                (profit[item.year]?.expenseYTDForecast || 0),
                            ),
                          )})`
                      : (profit[item.year]?.incomeYTDBudget || 0) -
                          (profit[item.year]?.expenseYTDBudget || 0) >=
                        0
                      ? thousandFormat2(
                          (profit[item.year]?.incomeYTDBudget || 0) -
                            (profit[item.year]?.expenseYTDBudget || 0),
                        )
                      : `(${thousandFormat2(
                          Math.abs(
                            (profit[item.year]?.incomeYTDBudget || 0) -
                              (profit[item.year]?.expenseYTDBudget || 0),
                          ),
                        )})`}
                  </b>
                </Grid>
              </div>
            ))}
          </Grid>

          {/* % profit */}
          <Grid container>
            <div
              style={{
                ...tableBorder,
                borderRight: 'none',
                display: 'flex',
                width:
                  selectedYearLength === 0
                    ? 'calc(50% - 8px)'
                    : `calc(${100 / selectedYearLength}% - 0px)`,
                justifyContent: 'start',
                backgroundColor: '#f7f7f7',
              }}
            >
              <TextField
                sx={{
                  height: 25,
                  '& .MuiInputBase-input': {
                    height: 25,
                    padding: 0,
                    fontWeight: 'bold',
                    fontSize: 12,
                    fontFamily: 'Verdana !important',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderLeft: 'none',
                  },
                }}
                value={pprofitName}
                onChange={(e) => handleChangeProfitName(e, 'percent')}
                onBlur={() => handleSubmitProfitName('percent')}
                fullWidth
              />
            </div>
            {selectedItems.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  width:
                    selectedYearLength === 0
                      ? 'calc(50% - 8px)'
                      : `calc(${100 / selectedYearLength}% - 0px)`,
                  backgroundColor: index % 2 === 0 ? '#f7f7f7' : '#f0f0f0',
                }}
              >
                <Grid
                  size={6}
                  style={tableBorder}
                  onClick={(event) => {
                    handleElement(event, 'table');
                  }}
                  onMouseMove={(event) => {
                    if (tooltipTimeout) clearTimeout(tooltipTimeout);
                    showTooltipHandler(
                      event,
                      pprofit[item.year].YTDActual,
                      item.year,
                      '% Profit',
                    );
                  }}
                  onMouseLeave={(event) => {
                    tooltipTimeout = setTimeout(() => {
                      hideTooltipHandler();
                    }, 300);
                  }}
                  onTouchMove={(event) => {
                    if (tooltipTimeout) clearTimeout(tooltipTimeout);
                    showTooltipHandler(
                      event,
                      pprofit[item.year].YTDActual,
                      item.year,
                      '% Profit',
                    );
                  }}
                  onTouchEnd={(event) => {
                    tooltipTimeout = setTimeout(() => {
                      hideTooltipHandler();
                    }, 300);
                  }}
                >
                  <b>
                    {(pprofit[item.year]?.YTDActual || 0) >= 0
                      ? `${pprofit[item.year]?.YTDActual || 0}%`
                      : `(${Math.abs(pprofit[item.year]?.YTDActual || 0)}%)`}
                  </b>
                </Grid>
                <Grid
                  size={6}
                  style={tableBorder}
                  onClick={(event) => {
                    handleElement(event, 'table');
                  }}
                  onMouseMove={(event) => {
                    if (tooltipTimeout) clearTimeout(tooltipTimeout);
                    showTooltipHandler(
                      event,
                      selectedOption === 'Forecast'
                        ? pprofit[item.year].YTDForecast
                        : pprofit[item.year].YTDBudget,
                      item.year,
                      '% Profit',
                    );
                  }}
                  onMouseLeave={(event) => {
                    tooltipTimeout = setTimeout(() => {
                      hideTooltipHandler();
                    }, 300);
                  }}
                  onTouchMove={(event) => {
                    if (tooltipTimeout) clearTimeout(tooltipTimeout);
                    showTooltipHandler(
                      event,
                      selectedOption === 'Forecast'
                        ? pprofit[item.year].YTDForecast
                        : pprofit[item.year].YTDBudget,
                      item.year,
                      '% Profit',
                    );
                  }}
                  onTouchEnd={(event) => {
                    tooltipTimeout = setTimeout(() => {
                      hideTooltipHandler();
                    }, 300);
                  }}
                >
                  <b>
                    {selectedOption === 'Forecast'
                      ? (pprofit[item.year]?.YTDForecast || 0) >= 0
                        ? `${pprofit[item.year]?.YTDForecast || 0}%`
                        : `(${Math.abs(pprofit[item.year]?.YTDForecast || 0)}%)`
                      : (pprofit[item.year]?.YTDBudget || 0) >= 0
                      ? `${pprofit[item.year]?.YTDBudget || 0}%`
                      : `(${Math.abs(pprofit[item.year]?.YTDBudget || 0)}%)`}
                  </b>
                </Grid>
              </div>
            ))}
          </Grid>
          {tooltipOpen && (
            <TooltipWithBounds
             
              top={tooltipTop + pageYOffset}
              left={tooltipLeft}
            >
              <div className="bkg-white color-black" style={{ minWidth: 200 }}>
                <div className="ft-12">
                  FY: <strong>{tooltipData['FY']}</strong>
                </div>
                {tooltipData['IncomeStatementCategory'] !== undefined ? (
                  <div className="ft-12">
                    IncomeStatementCategory:{' '}
                    <strong>{tooltipData['IncomeStatementCategory']}</strong>
                  </div>
                ) : (
                  ''
                )}
                <div className="ft-12">
                  {tooltipData.disp}:
                  <strong>
                    {tooltipData.disp === 'percent' ? '' : '$'}
                    {thousandFormat(tooltipData['data'])}
                    {tooltipData.disp === 'percent' ? '%' : ''}
                  </strong>
                </div>
              </div>
            </TooltipWithBounds>
          )}
        </div>
      ) : (
        <div style={empty(200)} className="well">
          No data
        </div>
      )}
    </div>
  );
};

BottomChart.propTypes = {
  detailData: PropTypes.array.isRequired,
  handleFilter: PropTypes.func.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedOption: PropTypes.string.isRequired,
  showPercent: PropTypes.bool.isRequired,
  profitName: PropTypes.string,
  pprofitName: PropTypes.string,
  tableStructure: PropTypes.array,
};

export default BottomChart;
